import { useEffect, useState, type ReactNode } from "react";
import { Icon, type IconName } from "./Icons";

const HERO_PREVIEW_CARDS = [
  { name: "Spark", meta: "Tempo / Burn", pip: "R", hue: "warm", art: "/spark.png" },
  { name: "Bloom", meta: "Value Engine", pip: "G", hue: "mint", art: "/bloom.png" },
  { name: "Veil", meta: "Control Shell", pip: "U", hue: "cool", art: "/veil.png" },
];

export function PageIntro({
  eyebrow,
  title,
  body,
  icon = "sparkles",
  actions,
  align = "left",
}: {
  eyebrow: string;
  title: ReactNode;
  body?: ReactNode;
  icon?: IconName;
  actions?: ReactNode;
  align?: "left" | "center";
}) {
  return (
    <section className={`page-intro page-intro--${align}`}>
      <span className="eyebrow">
        <span className="dot" />
        <Icon name={icon} size={14} />
        {eyebrow}
      </span>
      <div className="page-intro-main">
        <div>
          <h1>{title}</h1>
          {body && <p className="page-intro-copy">{body}</p>}
        </div>
        {actions && <div className="page-intro-actions">{actions}</div>}
      </div>
    </section>
  );
}

export function Surface({
  children,
  className = "",
  tone = "default",
}: {
  children: ReactNode;
  className?: string;
  tone?: "default" | "strong" | "foil";
}) {
  return <div className={`surface surface--${tone} ${className}`.trim()}>{children}</div>;
}

export function BentoCard({
  icon,
  title,
  children,
  className = "",
  feature = false,
}: {
  icon: IconName;
  title: ReactNode;
  children: ReactNode;
  className?: string;
  feature?: boolean;
}) {
  return (
    <article className={`bento-card${feature ? " bento-card--feature" : ""} ${className}`.trim()}>
      <span className="bento-icon">
        <Icon name={icon} />
      </span>
      <h3>{title}</h3>
      <div className="bento-body">{children}</div>
    </article>
  );
}

export function MetricCard({
  label,
  value,
  detail,
  icon = "activity",
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  icon?: IconName;
}) {
  return (
    <div className="metric-card">
      <span className="metric-icon">
        <Icon name={icon} />
      </span>
      <span className="metric-label">{label}</span>
      <strong>{value}</strong>
      {detail && <span className="metric-detail">{detail}</span>}
    </div>
  );
}

export function EmptyState({
  icon = "cards",
  title,
  body,
  action,
}: {
  icon?: IconName;
  title: ReactNode;
  body: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-state-icon">
        <Icon name={icon} size={24} />
      </span>
      <h3>{title}</h3>
      <p>{body}</p>
      {action && <div className="empty-state-action">{action}</div>}
    </div>
  );
}

export function HeroDeckPreview() {
  const [positionOffset, setPositionOffset] = useState(0);

  useEffect(() => {
    const shouldKeepStatic =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(max-width: 760px)").matches;

    if (shouldKeepStatic) {
      return;
    }

    const swapTimer = window.setInterval(() => {
      setPositionOffset((current) => (current + 1) % HERO_PREVIEW_CARDS.length);
    }, 4200);

    return () => window.clearInterval(swapTimer);
  }, []);

  return (
    <div className="hero-product" aria-hidden="true">
      <div className="hero-product-ring" />
      <div className="hero-card-stack">
        {HERO_PREVIEW_CARDS.map((card, index) => {
          const position = ((index + positionOffset) % HERO_PREVIEW_CARDS.length) + 1;

          return (
            <div
              key={card.name}
              className={`hero-preview-card hero-preview-card--position-${position} hero-preview-card--${card.hue}`}
            >
              <div className="hero-preview-art">
                <img src={card.art} alt={`${card.name} preview art`} />
                <span>{card.pip}</span>
              </div>
              <div className="hero-preview-lines">
                <strong>{card.name}</strong>
                <span>{card.meta}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="hero-command-panel">
        <div className="hero-command-top">
          <span className="hero-command-dot" />
          AI deck synthesis
        </div>
        <div className="hero-command-line long" />
        <div className="hero-command-line mid" />
        <div className="hero-command-stats">
          <span>Curve OK</span>
          <span>Mana 92%</span>
          <span>Legal</span>
        </div>
      </div>
    </div>
  );
}
